import { useState } from 'react';
import { Check, Copy } from 'lucide-react';

interface Props {
  /** The text placed on the clipboard when clicked. */
  text: string;
  /** Optional visible label next to the icon (e.g. "Copy"). Icon-only when omitted. */
  label?: string;
  /** Tooltip / accessible name. */
  title?: string;
  className?: string;
  /** Render light (for use on a dark/coloured bubble). */
  tone?: 'default' | 'onDark';
}

/**
 * Copy-to-clipboard control with a brief "copied" confirmation.
 *
 * Uses the async Clipboard API (available on https and on localhost, which
 * counts as a secure context) and falls back to a hidden-textarea +
 * execCommand for older/insecure origins so it works in every dev setup.
 */
export default function CopyButton({
  text,
  label,
  title = 'Copy',
  className = '',
  tone = 'default',
}: Props) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked (permissions / unsupported) — fail quietly.
    }
  };

  const toneClasses =
    tone === 'onDark'
      ? 'text-white/70 hover:text-white hover:bg-white/15'
      : 'text-neutral-500 hover:text-neutral-800 hover:bg-neutral-100';

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={copied ? 'Copied' : title}
      aria-label={copied ? 'Copied' : title}
      className={`inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs font-medium transition-colors ${toneClasses} ${className}`}
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {label && <span>{copied ? 'Copied' : label}</span>}
    </button>
  );
}
